import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import type { ComponentProps } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";

/**
 * Every size here clears 44px of touch target. `default` and `icon` do it
 * outright at `h-11`; `sm` keeps a 36px *paint* and extends its hit area
 * to 44 with a pseudo-element, the same trade `IconButton` makes for the
 * design's 34px circles.
 *
 * **An earlier version of this comment claimed `sm` cleared 44px "through
 * its padding plus the row height it sits in". It did not.** `px-3` is
 * horizontal, and the row a button sits in is not part of the button's hit
 * area — a taller row does not make a 36px control tappable above itself.
 * `sm` measured 36px tall on every route until DEE-85 pointed a ruler at
 * it. The lesson is not about buttons: a written justification is not a
 * measurement, and this file carried one for months.
 */
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-control text-sm font-medium " +
    "transition-colors disabled:pointer-events-none disabled:opacity-50 " +
    "[&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary-hover",
        secondary: "bg-surface text-foreground border border-border-strong hover:bg-surface-muted",
        ghost: "text-foreground hover:bg-surface-muted",
        danger: "bg-danger text-danger-foreground hover:bg-danger-hover",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-4 py-2",
        // 36px of paint, 44px of target. The pseudo-element is centred on
        // the button and takes no part in layout, so nothing around it
        // moves — which is the reason for doing it this way rather than
        // growing the button to `h-11`. `touch-targets.spec.ts` measures
        // the union of the two, and clicks 4px above the paint to prove
        // the extension is real rather than merely measurable.
        sm: [
          // `min-w-11` too: at 375 the account trigger hides its email
          // and was 40px wide with a perfectly good 44px height — the
          // rule is 44×44, and half of it passing is not it passing.
          "relative h-9 min-w-11 px-3",
          "after:-translate-y-1/2 after:absolute after:inset-x-0 after:top-1/2",
          "after:h-11 after:content-['']",
        ].join(" "),
        icon: "size-11",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

export interface ButtonProps
  // `ComponentProps<"button">` rather than `ButtonHTMLAttributes`, so
  // `ref` is a plain prop — React 19 dropped the need for `forwardRef`,
  // and `ConfirmDialog` needs a ref to focus Cancel.
  extends ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  /** Render as the child element — a router `<Link>`, usually. */
  asChild?: boolean;
  /**
   * What this button *does*, shown on hover and on keyboard focus.
   *
   * **Say something the label does not.** "Approve" already says
   * approve; the tooltip is for what happens next — that it publishes the
   * change to everyone who can read the type, and that it cannot be
   * undone. A tooltip that repeats its own trigger is noise, and it makes
   * a screen reader announce the same words twice.
   *
   * Distinct from `HintedButton`'s `hint`, which explains why a control
   * will **not** work and is rendered as visible text plus
   * `aria-describedby` rather than as a tooltip — the contract's "never
   * put a tooltip on a disabled control" rule. This one is for a control
   * that works.
   */
  tooltip?: string | undefined;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  tooltip,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot : "button";
  const button = (
    <Component className={cn(buttonVariants({ variant, size }), className)} {...props} />
  );

  // No wrapper at all when there is nothing to say, so a button without a
  // tooltip renders exactly the markup it always did and pays nothing.
  if (tooltip === undefined) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
